import { useCallback, useEffect, useRef } from 'react';
import {
  CommunicationType,
  Country,
  DayOfWeek,
  PhoneType,
  Status
} from '@grc/ui-shared/lib/components/models/enums/enums';
import useGetApiData from '../services/get-api-data';
import GetEquipmentsById from '../services/get-equipments-by-id';
import GetCurrencyDetails from '../services/get-currency-details';
import Body from './body';
import Footer from './footer';
import Header from './header';
import SideNav from './side-nav';
import { useSetCarrierPortalGlobalStorePath } from '../../stores/carrier-portal-global-store';
import { useI18nState } from '../../stores/settings/language-context';
import {
  defaultScheduleDay,
  initialBusinessHours,
  initialContactFormValues,
  initialContactsList,
  initialEquipment,
  initialTracking,
  intialPreAssignedReferences
} from '../../stores/carrierPortalSettingStore';
import { IComplianceStore } from '../../stores/complianceStore';
import {
  carriersUrl,
  tenantService,
  transportationModeUrl,
  businessEntityUrl,
  contactsUrl,
  portalAdapter,
  carrierPortalHeader,
  unitsOfMeasuresUrl,
  shipmentTypesUrl,
  serviceLevelUrl,
  equipmentTypeUrl,
  referenceTypeUrl
} from '../../common/constants/url-constants';
import notify from '../common/notify';
import {
  IBusinessHours,
  ICarrierPortalSettings,
  IIntegrationDetails,
  IContactColumns,
  IModesSupported,
  IPreAssignedReferences,
  ITenderSetting,
  ITracking,
  IPaymentAndInvoicing,
  IGeoFence
} from '../../models/settings.interface';
import { ICurrencyDetails } from '../../models/currency-details.interface';
import { GrcNotify, NotifyType } from '@grc/ui-package';
import {
  BillingDaysLabels,
  InsuranceTypeLabels,
  InvoiceMethodLabels
} from '../services/transport-mapper';
import getBillToName from '../services/get-billTo-name';
import { IUnitsofMeasure } from '../../models/units-of-measure.interface';
import { ITransportationModes } from '../../models/transportation-mode.interface';
import {
  IEquipmentTypes,
  IServiceLevels,
  IShipmentTypes
} from '../../models/mode-and-equipment.interface';
import { IReferences } from '../../models/references-and-documents.interface';
import { getBusinessEntities } from '../services/get-business-entities';
import fetchLocationGeoFenceDetails from '../services/fetch-location-geo-fence-details-byid';

const Layout = () => {
  const setCarrierDetailsStore = useSetCarrierPortalGlobalStorePath(
    store => store.carrierDetails
  );
  const setCarrierSettingDetailsStore = useSetCarrierPortalGlobalStorePath(
    store => store.carrierportalSetting
  );
  const setCurrencyDetailsStore = useSetCarrierPortalGlobalStorePath(
    store => store.currencyDetails
  );
  const setComplianceStore = useSetCarrierPortalGlobalStorePath(
    store => store.compliance
  );
  const setUnitsOfMeasureStore = useSetCarrierPortalGlobalStorePath(
    store => store.unitsOfMeasure
  );
  const setTransportationModesStore = useSetCarrierPortalGlobalStorePath(
    store => store.transportationModes
  );
  const setShipmentTypeStore = useSetCarrierPortalGlobalStorePath(
    store => store.shipmentTypes
  );
  const setServiceLevelStore = useSetCarrierPortalGlobalStorePath(
    store => store.serviceLevels
  );
  const setEquipmentTypeStore = useSetCarrierPortalGlobalStorePath(
    store => store.equipmentTypes
  );
  const setReferences = useSetCarrierPortalGlobalStorePath(
    store => store.references
  );
  const { fetchData } = useGetApiData();
  const { localeData } = useI18nState();
  // The startup fetches run from a mount-only effect, so they read the
  // current locale through a ref instead of a stale closure.
  const localeDataRef = useRef(localeData);
  localeDataRef.current = localeData;

  const getCarrierDetails = useCallback(async () => {
    try {
      const locationResponse: any = await fetchData(
        `${carriersUrl}`,
        notify,
        tenantService,
        undefined,
        portalAdapter,
        carrierPortalHeader
      );
      if (locationResponse?.data?.id) {
        if (locationResponse?.data?.id)
          fetchCarrierSettings(
            locationResponse?.data?.id,
            locationResponse?.data?.tenantId,
            locationResponse?.data
          );

        fetchUOMDetails(locationResponse?.data?.tenantId);
        fetchTransportationModes(locationResponse?.data?.tenantId);
        fetchShipmentTypes(locationResponse?.data?.tenantId);
        fetchServiceLevels(locationResponse?.data?.tenantId);
        fetchEquipmentTypes(locationResponse?.data?.tenantId);
        fetchReferencesSettings(locationResponse?.data?.tenantId);

        setCarrierDetailsStore({
          id: locationResponse?.data?.id,
          tenantId: locationResponse?.data?.tenantId,
          name: locationResponse?.data?.name,
          alias: locationResponse?.data?.alias,
          generalEmailAddress: locationResponse?.data?.generalEmailAddress,
          generalPhoneNumber: {
            phoneType: locationResponse?.data?.generalPhoneNumber?.phoneType,
            phoneNumber:
              locationResponse?.data?.generalPhoneNumber?.phoneNumber,
            displayOrder:
              locationResponse?.data?.generalPhoneNumber?.displayOrder
          },
          address: {
            streetAddress: locationResponse?.data?.address?.streetAddress ?? [],
            city: locationResponse?.data?.address?.city ?? '',
            provinceState: locationResponse?.data?.address?.provinceState ?? '',
            postalZipCode: locationResponse?.data?.address?.postalZipCode ?? '',
            country:
              locationResponse?.data?.address?.country ?? Country.UnitedStates,
            latitude: locationResponse?.data?.address?.latitude ?? 0,
            longitude: locationResponse?.data?.address?.longitude ?? 0
          },
          status: Status.Active,
          contacts: locationResponse?.data?.contacts,
          logoUrl: locationResponse?.data?.logoUrl,
          businessHours: locationResponse?.data?.businessHours,
          relationShips: locationResponse?.data?.relationships
        });
      }
    } catch (error: any) {
      const problem = error?.response?.data;

      GrcNotify({
        message: problem?.detail,
        status: NotifyType.Error
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData, setCarrierDetailsStore]);

  const fetchUOMDetails = useCallback(async (tenantId: string) => {
    try {
      const response = await fetchData(
        `${unitsOfMeasuresUrl}/${tenantId}`,
        notify,
        tenantService
      );
      if (!!response && response.status === 200) {
        const data: any = response.data;

        const mappedUnitOfMeasureDetails: IUnitsofMeasure[] = data?.map(
          (item: IUnitsofMeasure) => ({
            id: item.id ?? '',
            unit: item.unit ?? '',
            category: item.category ?? '',
            symbol: item.symbol ?? '',
            status: item.isActive ?? '',
            uomConversion: '',
            isDefault: item.isDefault ?? '',
            isActive: item.isActive ?? true
          })
        );

        setUnitsOfMeasureStore(mappedUnitOfMeasureDetails);
        return mappedUnitOfMeasureDetails;
      }
    } catch (error: any) {
      console.log('Failed to load unit of measure details:', error);
      GrcNotify({
        message: 'Failed to load unit of measure details',
        status: NotifyType.Error
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // transportation modes
  const fetchTransportationModes = useCallback(async (tenantId: string) => {
    try {
      const response = await fetchData(
        `${transportationModeUrl}/${tenantId}`,
        notify,
        tenantService
      );
      if (!!response && response.status === 200) {
        const data: any = response.data;

        const mappedTransportationModeDetails: ITransportationModes[] =
          data?.map((item: ITransportationModes) => ({
            id: item.id ?? '',
            displayName: item.displayName ?? '',
            tenderResponseTime: item.tenderResponseTime ?? '',
            ediCodes: item.ediCodes ?? '',
            isActive: item.isActive ?? true
          }));

        setTransportationModesStore(mappedTransportationModeDetails);
        return mappedTransportationModeDetails;
      }
    } catch (error) {
      console.error('Failed to load transportation mode details:', error);
      GrcNotify({
        message: 'Failed to load transportation mode details',
        status: NotifyType.Error
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // shipment types
  const fetchShipmentTypes = useCallback(async (tenantId: string) => {
    try {
      const response = await fetchData(
        `${shipmentTypesUrl}/${tenantId}`,
        notify,
        tenantService
      );
      if (!!response && response.status === 200) {
        const data: any = response.data;

        const mappedShipmentTypeDetails: IShipmentTypes[] = data?.map(
          (item: IShipmentTypes) => ({
            id: item.id ?? '',
            internalId: item.internalId ?? '',
            shipmentTypeName: item.shipmentTypeName ?? '',
            isDefault: item.isDefault ?? true,
            isActive: item.isActive ?? true
          })
        );

        setShipmentTypeStore(mappedShipmentTypeDetails);
        return mappedShipmentTypeDetails;
      }
    } catch (error) {
      console.error('Failed to load shipment type details:', error);
      GrcNotify({
        message: 'Failed to load shipment type details',
        status: NotifyType.Error
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // fetch service levels
  const fetchServiceLevels = useCallback(async (tenantId: string) => {
    try {
      const response = await fetchData(
        `${serviceLevelUrl}/${tenantId}`,
        notify,
        tenantService
      );
      if (!!response && response.status === 200) {
        const data: any = response.data;

        const mappedServiceLevelDetails: IServiceLevels[] = data?.map(
          (item: IServiceLevels) => ({
            id: item.id ?? '',
            serviceChargeName: item.serviceChargeName ?? '',
            isStandardService: item.isStandardService ?? true,
            description: item.description ?? '',
            isDefault: item.isDefault ?? true,
            isActive: item.isActive ?? true
          })
        );

        setServiceLevelStore(mappedServiceLevelDetails);
        return mappedServiceLevelDetails;
      }
    } catch (error) {
      console.error('Failed to load service level details:', error);
      GrcNotify({
        message: localeDataRef.current.FailedToLoadServiceLevelDetails,
        status: NotifyType.Error
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // fetch references
  const fetchReferencesSettings = useCallback(async (tenantId: string) => {
    try {
      const response = await fetchData(
        `${referenceTypeUrl}/${tenantId}/false`,
        notify,
        tenantService
      );
      if (!!response && response.status === 200) {
        const data: any = response.data;

        const mappedReferencesDetails: IReferences[] = data?.map(
          (item: IReferences) => ({
            id: item.id ?? '',
            name: item.name ?? '',
            isActive: item.isActive ?? true
          })
        );

        setReferences(mappedReferencesDetails);
        return mappedReferencesDetails;
      }
    } catch (error) {
      console.error('Failed to load references details:', error);
      GrcNotify({
        message: 'Failed to load references details',
        status: NotifyType.Error
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // fetch equipment types
  const fetchEquipmentTypes = useCallback(async (tenantId: string) => {
    try {
      const response = await fetchData(
        `${equipmentTypeUrl}/${tenantId}/false`,
        notify,
        tenantService
      );

      if (!!response && response.status === 200) {
        const data: any = response.data;
        const mappedEquipmentTypes: IEquipmentTypes[] = data?.map(
          (item: IEquipmentTypes) => ({
            id: item.id ?? '',
            equipmentTypeName: item.equipmentTypeName ?? '',
            equipmentGroup: item.equipmentGroup ?? '',
            isActive: item.isActive ?? true
          })
        );

        setEquipmentTypeStore(mappedEquipmentTypes);
        return mappedEquipmentTypes;
      }
    } catch (error) {
      console.error('Failed to load equipment type details:', error);
      GrcNotify({
        message: localeDataRef.current.FailedToLoadEquipmentTypeDetails,
        status: NotifyType.Error
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCarrierSettings = useCallback(
    async (carrierId: string, tenantId: string, locationResponseData: any) => {
      const res = await fetchData(
        `${carriersUrl}/${carrierId}`,
        notify,
        tenantService
      );
      const data: any = res?.data ?? [];
      const equipmentList: any[] = [];

      if (Array.isArray(data?.equipment) && data?.equipment?.length > 0) {
        for (const item of data?.equipment) {
          try {
            const equipmentTypeId = Object.keys(item.equipmentTypes ?? {})[0];
            const grossWeight = Object.values(item.equipmentTypes ?? {})[0];
            const equipmentName = await GetEquipmentsById({
              tenantId: tenantId,
              equipmentTypeId: equipmentTypeId, // use first key from equipmentTypes object
              fetchData,
              localeData: localeDataRef.current
            });

            equipmentList.push({
              id: item.id ?? '',
              equipmentName: equipmentName ?? '',
              isActive: item.isActive ?? false,
              grossWeight: grossWeight
            });
          } catch (error) {
            console.error('Error fetching equipment name:', error);
            equipmentList.push({
              id: item.id ?? '',
              equipmentName: '',
              isActive: item.isActive ?? false,
              grossWeight: ''
            });
          }
        }
      }

      const carrierTracking = data?.carrierTracking;
      let mappedTracking: ITracking[] = [...initialTracking];
      if (Array.isArray(carrierTracking) && carrierTracking.length > 0) {
        mappedTracking = carrierTracking.slice(0, 3).map((item: any) => ({
          trackingMethod: item.TrackingMethod ?? item.trackingMethod,
          trackingMethodPriority:
            item.TrackingMethodPriority ?? item.trackingMethodPriority,
          trackingReferenceType:
            item.TrackingReferenceType ?? item.trackingReferenceType,
          checkCallInterval: item.CheckCallInterval ?? item.checkCallInterval
        }));
        if (mappedTracking.length < 2) {
          mappedTracking.push({
            trackingMethod: undefined,
            trackingMethodPriority: undefined,
            trackingReferenceType: undefined,
            checkCallInterval: undefined
          });
        }
      }

      const carrierPreAssignedReferences =
        data?.carrierPreAssignedReferences ||
        data?.CarrierPreAssignedReferences;
      const proNumbers =
        carrierPreAssignedReferences?.ProNumbers ||
        carrierPreAssignedReferences?.proNumbers ||
        [];
      const mappedPreAssignedReferences: IPreAssignedReferences = {
        ...intialPreAssignedReferences,
        proNumbers:
          proNumbers.length > 0
            ? proNumbers.map((item: any) => ({
                from: item.From ?? item.from ?? '',
                to: item.To ?? item.to ?? ''
              }))
            : [{ from: '', to: '' }]
      };

      const modeIds = data?.basicInformation?.transportationModeIds ?? [];
      let mappedModesSupported: IModesSupported[] = [];

      try {
        // Fetch all transportation modes for tenant once
        const resp = await fetchData(
          `${transportationModeUrl}/${tenantId}`,
          notify,
          tenantService
        );

        if (resp?.status === 200 && Array.isArray(resp.data)) {
          const allModes = resp.data;
          // Map mode IDs to names
          mappedModesSupported = modeIds
            .map((id: string) => {
              const found = allModes.find((m: any) => m.id === id);
              return {
                id: found?.id ?? '',
                name: found?.displayName ?? ''
              };
            })
            .filter(Boolean);
        }
      } catch (error) {
        console.error('Error fetching transportation modes:', error);
      }

      let mappedBusinessHours: IBusinessHours = { ...initialBusinessHours };
      try {
        const businessHoursResponse: any = await fetchData(
          `${businessEntityUrl}?entityId=${carrierId}`,
          notify,
          tenantService,
          { status: true }
        );

        if (
          businessHoursResponse?.status === 200 &&
          businessHoursResponse?.data
        ) {
          const operationScheduleData =
            businessHoursResponse?.data?.[0]?.operationSchedule;

          if (operationScheduleData?.id) {
            const { businessHours: fetchedBusinessHours } =
              operationScheduleData;

            if (fetchedBusinessHours) {
              const dailySchedules = fetchedBusinessHours.dailySchedules || {};
              let hasSaturdayIntervals = false;
              let hasSundayIntervals = false;

              const saturdayValue =
                dailySchedules.Saturday?.timeIntervals || [];
              const sundayValue = dailySchedules.Sunday?.timeIntervals || [];

              hasSaturdayIntervals =
                saturdayValue.length > 0 &&
                saturdayValue.some(
                  (interval: any) =>
                    interval?.openTime !== '00:00:00' &&
                    interval?.closeTime !== '00:00:00'
                );
              hasSundayIntervals =
                sundayValue.length > 0 &&
                sundayValue.some(
                  (interval: any) =>
                    interval?.openTime !== '00:00:00' &&
                    interval?.closeTime !== '00:00:00'
                );

              const mappedDailySchedules = Object.keys(dailySchedules).reduce(
                (acc, dayKey) => {
                  const dayEnumValue =
                    DayOfWeek[dayKey as keyof typeof DayOfWeek];
                  if (dayEnumValue !== undefined) {
                    acc[dayEnumValue] = dailySchedules[dayKey];
                  }
                  return acc;
                },
                {} as any
              );

              mappedBusinessHours = {
                id: operationScheduleData.id || '',
                locationId: carrierId,
                businessHours: {
                  ...fetchedBusinessHours,
                  dailySchedules: mappedDailySchedules
                },
                shippingHours: operationScheduleData?.shippingHours
                  ? {
                      ...operationScheduleData.shippingHours,
                      dailySchedules: Object.keys(
                        operationScheduleData.shippingHours.dailySchedules || {}
                      ).reduce((acc, dayKey) => {
                        const dayEnumValue =
                          DayOfWeek[dayKey as keyof typeof DayOfWeek];
                        if (dayEnumValue !== undefined) {
                          acc[dayEnumValue] =
                            operationScheduleData.shippingHours.dailySchedules[
                              dayKey
                            ];
                        }
                        return acc;
                      }, {} as any)
                    }
                  : initialBusinessHours.shippingHours,
                receivingHours: operationScheduleData?.receivingHours
                  ? {
                      ...operationScheduleData.receivingHours,
                      dailySchedules: Object.keys(
                        operationScheduleData.receivingHours.dailySchedules ||
                          {}
                      ).reduce((acc, dayKey) => {
                        const dayEnumValue =
                          DayOfWeek[dayKey as keyof typeof DayOfWeek];
                        if (dayEnumValue !== undefined) {
                          acc[dayEnumValue] =
                            operationScheduleData.receivingHours.dailySchedules[
                              dayKey
                            ];
                        }
                        return acc;
                      }, {} as any)
                    }
                  : initialBusinessHours.receivingHours,
                isSaturday: hasSaturdayIntervals,
                isSunday: hasSundayIntervals
              };
            }
          }
        }
      } catch (error) {
        console.error('Error fetching business hours:', error);
      }

      let mappedContactsList: IContactColumns[] = [...initialContactsList];
      try {
        const params = new URLSearchParams({ status: 'true' });
        if (carrierId) params.append('contactForEntityId', carrierId);

        const contactsUrlWithParams = `${contactsUrl}?${params.toString()}`;
        const contactsResponse: any = await fetchData(
          contactsUrlWithParams,
          notify,
          tenantService
        );

        if (
          contactsResponse?.status === 200 &&
          Array.isArray(contactsResponse.data)
        ) {
          const locationIds = Array.from(
            new Set(
              contactsResponse.data.map(
                (contact: any) => contact?.primaryLocationId
              )
            )
          ).filter(Boolean);

          let locationMap: Record<string, string> = {};
          if (locationIds.length > 0) {
            try {
              const locations = await getBusinessEntities(fetchData, {
                locationIds: locationIds
                //status: true
              });
              locationMap = (locations?.data ?? []).reduce(
                (acc: Record<string, string>, location: any) => {
                  if (location?.id && location?.name) {
                    acc[location.id] = location.name;
                  }
                  return acc;
                },
                {}
              );
            } catch (locationError) {
              console.error('Error fetching locations:', locationError);
            }
          }

          mappedContactsList = contactsResponse.data.map((contact: any) => {
            const primaryLocationId = contact?.primaryLocationId;
            const locationName = primaryLocationId
              ? locationMap[primaryLocationId] || ''
              : '';
            const phoneNumber =
              contact?.telephone ||
              (Array.isArray(contact?.phoneNumbers) &&
              contact.phoneNumbers.length > 0
                ? contact.phoneNumbers[0]?.phoneNumber || ''
                : '');

            return {
              id: contact?.contactId || contact?.id || '',
              name: contact?.name || '',
              title: contact?.title || '',
              locationName: locationName,
              telephone: phoneNumber,
              email: contact?.email || '',
              address: contact?.address || '',
              orgInfo: contact?.orgInfo || ''
            };
          });
        }
      } catch (error) {
        console.error('Error fetching contacts:', error);
      }
      const currencyDetails: ICurrencyDetails[] = await GetCurrencyDetails(
        fetchData,
        true,
        localeDataRef.current
      );

      const mappedTenderSettings: ITenderSetting[] =
        data?.tenderingSettings?.map((item: any) => ({
          id: item.tenderSettingId ?? '',
          transportationModeId: item.transportationModeId ?? '',
          isAutoAccept: item.isAutoAccept ?? false,
          tenderResponsiveTime: item.tenderResponsiveTime ?? '',
          tenderMethods: item.tenderMethods,
          tenderContactId: item.tenderContactId ?? '',
          tenderContactName: '',
          advanceTenderNotificationInDays:
            item.advanceTenderNotificationInDays ?? '',
          tenderContactInfo: null,
          tenderWindowTime: {
            isCustomWorkingHours:
              item.tenderWindowTime.isCustomWorkingHours ?? false,
            isOpen24Hours: item.tenderWindowTime.isOpen24Hours ?? false,
            dailySchedules: item.tenderWindowTime.dailySchedules ?? {
              [DayOfWeek.Sunday]: defaultScheduleDay,
              [DayOfWeek.Monday]: defaultScheduleDay,
              [DayOfWeek.Tuesday]: defaultScheduleDay,
              [DayOfWeek.Wednesday]: defaultScheduleDay,
              [DayOfWeek.Thursday]: defaultScheduleDay,
              [DayOfWeek.Friday]: defaultScheduleDay,
              [DayOfWeek.Saturday]: defaultScheduleDay
            }
          },
          tenderRateType: item.tenderRateType ?? null
        }));

      const mappedComplianceSettings: IComplianceStore = {
        insuranceRecords: (data.compliance.insurances || []).map(
          (item: any) => ({
            insuranceType: InsuranceTypeLabels[item?.insuranceType],
            additionalInsured: {
              additionalInsuredName:
                item?.additionalInsured.additionalInsuredName,
              additionalInsuredId: item?.additionalInsured.additionalInsuredId
            },
            companyName: item?.companyName,
            policyNumber: item?.policyNumber,
            contactName: item?.contactName,
            contactPhoneNumber: item?.contactPhoneNumber,
            contactCountry: item?.contactCountry,
            policyAmount: {
              policyAmountCurrency: item?.policyAmount.policyAmountCurrency,
              policyAmountValue: item?.policyAmount.policyAmountValue
            },
            renewalReminderInDays: item?.renewalReminderInDays,
            effectiveDate: item?.effectiveDate,
            expirationDate: item?.expirationDate,
            policyDocument: item?.policyDocument,
            isActive: item?.isActive ?? true
          })
        ),
        licensingRecords: (data.compliance.licenses || []).map((item: any) => ({
          operatingAuthorityAndLicensing: item?.operatingAuthorityAndLicensing,
          licenseType: item?.licenseType,
          licenseName: item?.licenseName,
          licenseNumber: item?.licenseNumber,
          effectiveDate: item?.effectiveDate,
          expirationDate: item?.expirationDate,
          licenseDocument: item?.licenseDocument,
          isActive: item?.isActive ?? true
        })),
        emissionsInfo: {
          minorityCarrier: data.compliance.minorityCarrier ?? false,
          environmentalCertification:
            data.compliance.haveEnvironmentalCertificate ?? false,
          carbonEmissionRating: data.compliance.carbonEmissionRating,
          safetyRating: data.compliance.safetyRating
        },
        showInsurancePopup: false,
        selectedInsurance: null,
        showLicensingPopup: false,
        selectedLicense: null,
        activeSection: 'insurance',
        isLoading: false,
        error: null
      };

      const mappedCurrencyDetails: ICurrencyDetails[] = currencyDetails?.map(
        (item: ICurrencyDetails) => ({
          id: item.id ?? '',
          country: item.country ?? '',
          isoCode: item.isoCode ?? '',
          isActive: item.isActive ?? true,
          countryFlag: item.countryFlag ?? '',
          currencyName: item.currencyName ?? '',
          currencySymbol: item.currencySymbol ?? '',
          currencySymbolOnRight: item.currencySymbolOnRight ?? false
        })
      );

      const mappedIntegrationDetails: IIntegrationDetails = {
        isTrackingApiSetup:
          data?.integrationDetails?.isTrackingApiSetup ?? false,
        isTrackingEdiSetup:
          data?.integrationDetails?.isTrackingEdiSetup ?? false,
        isEdiInvoicingConfigured:
          data?.integrationDetails?.isEdiInvoicingConfigured ?? false,
        isApiInvoicingConfigured:
          data?.integrationDetails?.isApiInvoicingConfigured ?? false,
        isApiIntegratedWithOtherSystems:
          data?.integrationDetails?.isApiIntegratedWithOtherSystems ?? false,
        isEdiConfiguredWithOtherSystems:
          data?.integrationDetails?.isEdiConfiguredWithOtherSystems ?? false
      };

      const mappedPaymentAndInvoicing: IPaymentAndInvoicing = {
        locationId: carrierId ?? '',
        invoiceMethod:
          InvoiceMethodLabels[data?.basicInformation?.invoiceMethod] ??
          InvoiceMethodLabels[1],
        invoicePartyName:
          (await getBillToName(
            fetchData,
            data?.basicInformation?.responsibleLocationId ?? ''
          )) ?? '',
        invoiceLocation:
          (await getBillToName(
            fetchData,
            data?.basicInformation?.billToId ?? ''
          )) ?? '',
        thirdPartyFreightPaymentCompany:
          (await getBillToName(
            fetchData,
            data?.basicInformation?.thirdPartyLocationId ?? ''
          )) ?? '',
        responsibleLocationId:
          data?.basicInformation?.responsibleLocationId ?? '',
        billToId: data?.basicInformation?.billToId ?? '',
        thirdPartyLocationId:
          data?.basicInformation?.thirdPartyLocationId ?? '',
        creditLimit: data?.basicInformation?.creditLimit ?? 0,
        creditDays: data?.basicInformation?.creditDays ?? 0,
        financialStabilityScore:
          data?.basicInformation?.financialStabilityScore ?? 0,
        billGenerationTerm:
          BillingDaysLabels[data?.basicInformation?.billGenerationTerm] ?? 1
      };

      const mappedGeoFenceDetails = async (carrierId: string) => {
        const geoFenceResponse: any = await fetchLocationGeoFenceDetails(
          carrierId,
          fetchData,
          notify,
          tenantService
        );

        return (
          geoFenceResponse?.map((item: IGeoFence) => ({
            id: item.id,
            name: item.name,
            fence: item.fence,
            checkCallType: item.checkCallType,
            description: item.description,
            displayColor: item.displayColor
          })) ?? []
        );
      };

      const mappedGeoFences = await mappedGeoFenceDetails(carrierId);

      const mappedData: ICarrierPortalSettings = {
        tracking: mappedTracking,
        preAssignedReferences: mappedPreAssignedReferences,
        businessHours: mappedBusinessHours,
        contacts: initialContactFormValues,
        contactsList: mappedContactsList,
        tenderSettings: mappedTenderSettings,
        integrationDetails: mappedIntegrationDetails,
        paymentAndInvoicing: mappedPaymentAndInvoicing,
        geoFences: mappedGeoFences,
        carrierSettingDetails: {
          addressInformation: {
            companyName: locationResponseData?.name ?? '',
            addressLine: locationResponseData?.address?.streetAddress ?? [],
            city: locationResponseData?.address?.city ?? '',
            state: locationResponseData?.address?.provinceState ?? '',
            zip: locationResponseData?.address?.postalZipCode ?? '',
            country: locationResponseData?.address?.country ?? '',
            latitude: locationResponseData?.address?.latitude ?? '',
            longitude: locationResponseData?.address?.longitude ?? '',
            timeZone: locationResponseData?.ianaTimezoneString ?? ''
          },
          locationInformation: {
            locationEmail: locationResponseData?.generalEmailAddress ?? '',
            telephoneNos: [
              {
                communicationType:
                  locationResponseData?.generalPhoneNumber?.phoneType ??
                  CommunicationType.Mobile,
                communicationAddressType:
                  locationResponseData?.generalPhoneNumber?.phoneType ??
                  PhoneType.Home,
                contactValue:
                  locationResponseData?.generalPhoneNumber?.phoneNumber ?? '',
                displayOrder:
                  locationResponseData?.generalPhoneNumber?.displayOrder ?? 0
              }
            ],
            websiteURL: locationResponseData?.logoUrl ?? '',
            branchCode: locationResponseData?.logoUrl ?? '',
            country: locationResponseData?.address?.country ?? '',
            timeZone: locationResponseData?.ianaTimezoneString ?? '',
            displayId: locationResponseData?.displayId ?? '',
            displayName: locationResponseData?.alias ?? ''
          },
          carrierBasicInfo: {
            scac: data?.basicInformation?.standardCarrierAlphaCode ?? '',
            mc: data.basicInformation?.motorCarrierNumber ?? '',
            dotIcc:
              data.basicInformation?.departmentOfTransportationNumber ?? '',
            federalTaxID: data.basicInformation?.federalTaxId ?? '',
            caatCode:
              data.basicInformation?.carrierHarmonizedAlphanumericCode ?? '',
            svii:
              data.basicInformation
                ?.safetyFitnessElectronicRecordsSystemIdentificationNumber ??
              '',
            cbsa: data.basicInformation?.canadaBorderServicesAgencyCode ?? '',
            modesSupported: mappedModesSupported,
            hazmatAuthorized:
              data.basicInformation?.emergencyContactInfo.isHazmatAuthorized ??
              false
          },
          emergencyContactInfo: {
            emergencyContactName:
              data.basicInformation?.emergencyContactInfo.name ?? '',
            emergencyContactPhone:
              (typeof data.basicInformation?.emergencyContactInfo
                .mobileNumber === 'string'
                ? data.basicInformation?.emergencyContactInfo.mobileNumber
                : data.basicInformation?.emergencyContactInfo.mobileNumber
                    ?.phoneNumber) ?? '',
            emergencyContactEmail:
              data.basicInformation?.emergencyContactInfo.emailAddress ?? '',
            hazmatAuthorized:
              data.basicInformation?.emergencyContactInfo.isHazmatAuthorized ??
              false
          }
        },
        equipment: equipmentList ?? [initialEquipment]
      };

      setCurrencyDetailsStore(mappedCurrencyDetails);
      setComplianceStore(mappedComplianceSettings);
      setCarrierSettingDetailsStore(mappedData);
      return mappedData;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchData, setCarrierSettingDetailsStore]
  );

  useEffect(() => {
    getCarrierDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="main-layout d-flex flex-row">
      <div className="side-nav">
        <SideNav />
      </div>
      <div className="main-layout-body-div w-100">
        <Header />
        <Body />
        <Footer />
      </div>
    </div>
  );
};

export default Layout;
